import React, { useCallback, useRef, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Snackbar,
    Typography
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CheckCircle as CheckIcon,
    Close as RemoveIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    Gavel as LawyerIcon,
    AccountBalance as ExecutorIcon
} from '@mui/icons-material';

type SignatureType = 'user_signature' | 'lawyer_signature' | 'executor_signature';

interface SignatureBlockProps {
    signatureType: SignatureType;
    signatureUrl: string | null;
    finalized: boolean;
    disabled?: boolean;
    disabledReason?: string;
    onUpload: (signatureType: SignatureType, file: File) => Promise<void>;
    onRemove?: (signatureType: SignatureType) => Promise<void>;
    onReplace?: (signatureType: SignatureType, file: File) => Promise<void>;
}

const LABELS: Record<SignatureType, { title: string; description: string; step: string }> = {
    user_signature: {
        step: 'Step 1 of 3 (Estate Owner)',
        title: 'User Signature',
        description: 'The will owner\'s signature to authorize the document.'
    },
    lawyer_signature: {
        step: 'Step 2 of 3 (Estate Lawyer)',
        title: 'Lawyer Signature',
        description: 'The legal representative\'s signature to verify the document.'
    },
    executor_signature: {
        step: 'Step 3 of 3 (Estate Executor)',
        title: 'Estate Executor Signature',
        description: 'The estate executor\'s signature to validate execution rights.'
    }
};

const ICONS: Record<SignatureType, React.ReactNode> = {
    user_signature: <PersonIcon />,
    lawyer_signature: <LawyerIcon />,
    executor_signature: <ExecutorIcon />
};

const ACCENT_COLORS: Record<SignatureType, { bg: string; border: string; glow: string }> = {
    user_signature: {
        bg: 'rgba(25, 118, 210, 0.06)',
        border: 'rgba(25, 118, 210, 0.25)',
        glow: 'rgba(25, 118, 210, 0.12)'
    },
    lawyer_signature: {
        bg: 'rgba(156, 39, 176, 0.06)',
        border: 'rgba(156, 39, 176, 0.25)',
        glow: 'rgba(156, 39, 176, 0.12)'
    },
    executor_signature: {
        bg: 'rgba(255, 152, 0, 0.06)',
        border: 'rgba(255, 152, 0, 0.25)',
        glow: 'rgba(255, 152, 0, 0.12)'
    }
};

/**
 * A memoized signature upload block.
 * Renders the current signature state (empty / uploaded / finalized)
 * and handles image upload via file input.
 */
const SignatureBlock: React.FC<SignatureBlockProps> = React.memo(({
    signatureType,
    signatureUrl,
    finalized,
    disabled = false,
    disabledReason,
    onUpload,
    onRemove,
    onReplace
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const label = LABELS[signatureType];
    const icon = ICONS[signatureType];
    const colors = ACCENT_COLORS[signatureType];
    const isSigned = !!signatureUrl;
    const isLocked = finalized || disabled;
    const canRemove = isSigned && !isLocked && !!onRemove;
    const isBusy = uploading || removing;

    const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload PNG, JPEG, WebP, or SVG.');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError('File is too large. Maximum size is 5MB.');
            return;
        }

        setUploading(true);
        setError(null);
        try {
            if (isSigned && onReplace) {
                await onReplace(signatureType, file);
            } else {
                await onUpload(signatureType, file);
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to upload signature. Please try again.');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isSigned, onReplace, onUpload, signatureType]);

    const handleRemove = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!onRemove || !canRemove || removing || uploading) return;
        setRemoving(true);
        setError(null);
        try {
            await onRemove(signatureType);
        } catch (err: any) {
            setError(err?.message || 'Failed to remove signature. Please try again.');
        } finally {
            setRemoving(false);
        }
    }, [canRemove, onRemove, removing, signatureType, uploading]);

    const handleClick = useCallback(() => {
        if (!isLocked && !isBusy) {
            fileInputRef.current?.click();
        }
    }, [isLocked, isBusy]);

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    backgroundColor: isSigned
                        ? 'rgba(46, 125, 50, 0.04)'
                        : colors.bg,
                    border: `1.5px ${isSigned ? 'solid' : 'dashed'} ${isSigned ? 'rgba(46, 125, 50, 0.3)' : colors.border}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: isLocked ? 'default' : 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': isLocked ? {} : {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${colors.glow}`,
                        borderColor: isSigned ? 'rgba(46, 125, 50, 0.5)' : colors.border.replace('0.25', '0.5')
                    },
                    '&::before': isLocked ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.01) 10px, rgba(0,0,0,0.01) 20px)',
                        pointerEvents: 'none'
                    } : {}
                }}
                onClick={handleClick}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSigned
                                ? 'rgba(46, 125, 50, 0.1)'
                                : colors.bg.replace('0.06', '0.14'),
                            color: isSigned ? '#2e7d32' : colors.border.replace('0.25', '1'),
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isSigned ? <CheckIcon /> : icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                            {label.step}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {label.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {label.description}
                        </Typography>
                    </Box>
                    {isLocked && (
                        <LockIcon
                            fontSize="small"
                            sx={{
                                color: 'rgba(0,0,0,0.26)',
                                animation: 'fadeIn 0.3s ease'
                            }}
                        />
                    )}
                    {!isLocked && canRemove && (
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            disabled={removing || uploading}
                            aria-label={`Remove ${label.title}`}
                            sx={{ color: 'rgba(0,0,0,0.4)' }}
                        >
                            {removing ? <CircularProgress size={16} /> : <RemoveIcon fontSize="small" />}
                        </IconButton>
                    )}
                </Box>

                {/* Signature Image or Upload Area */}
                {isSigned ? (
                    <Box
                        sx={{
                            mt: 1,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            border: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'center',
                            position: 'relative'
                        }}
                    >
                        <img
                            src={signatureUrl!}
                            alt={`${label.title}`}
                            style={{
                                maxHeight: 120,
                                maxWidth: '100%',
                                objectFit: 'contain',
                                filter: isLocked ? 'none' : undefined
                            }}
                        />
                        {!isLocked && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 1,
                                    color: 'text.secondary',
                                    fontStyle: 'italic'
                            }}
                        >
                            Click to replace signature
                        </Typography>
                        )}
                        {isLocked && disabledReason && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 1,
                                    color: 'text.secondary',
                                    fontStyle: 'italic'
                                }}
                            >
                                {disabledReason}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            mt: 1,
                            p: 3,
                            borderRadius: 2,
                            border: '1.5px dashed rgba(0,0,0,0.12)',
                            backgroundColor: 'rgba(0,0,0,0.01)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            minHeight: 100,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {uploading ? (
                            <>
                                <CircularProgress size={28} />
                                <Typography variant="caption" color="text.secondary">
                                    Uploading signature...
                                </Typography>
                            </>
                        ) : (
                            <>
                                <UploadIcon sx={{ fontSize: 32, color: 'rgba(0,0,0,0.25)' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {isLocked ? (disabledReason || 'Signature locked') : 'Click to upload signature image'}
                                </Typography>
                                {!isLocked && (
                                    <Typography variant="caption" color="text.disabled">
                                        PNG, JPEG, WebP, or SVG • Max 5MB
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={isLocked || isBusy}
                />
            </Paper>

            {/* Error snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setError(null)}
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
});

SignatureBlock.displayName = 'SignatureBlock';

export default SignatureBlock;
